import type { PageResponseDto } from '@/lib/api/event/types';
import { createHttpClient } from '@/lib/api/httpClient';
import { mapPlayerLook } from '@/lib/api/rewards-hub/mappers';
import type { PlayerLook } from '@/lib/api/rewards-hub/models';
import type { PlayerLookDto } from '@/lib/api/rewards-hub/types';
import { resolveStrapiBaseUrl } from './utils';

const BASE_URL = resolveStrapiBaseUrl();

const http = createHttpClient({ baseUrl: BASE_URL });

interface ProfileStatsDto {
  pv: string;
  vt: string;
  mt: number;
}

interface ProfileSearchItemDto {
  i: string;
  n: string;
  s: ProfileStatsDto | null;
  lk?: PlayerLookDto | null;
}

export interface ProfileStats {
  positionValue: number;
  volumeTraded: number;
  marketsTraded: number;
}

export interface ProfileSearchItem {
  id: string;
  nickname: string;
  stats: ProfileStats | null;
  look: PlayerLook | null;
}

export interface SearchProfilesProxyParams {
  q?: string;
  page?: number;
  size?: number;
}

export interface SearchProfilesProxyResult {
  profiles: ProfileSearchItem[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

function toFiniteNumber(value: string | number | null | undefined): number {
  if (value == null) return 0;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
}

function mapStats(dto: ProfileStatsDto | null): ProfileStats | null {
  if (!dto) return null;
  return {
    positionValue: toFiniteNumber(dto.pv),
    volumeTraded: toFiniteNumber(dto.vt),
    marketsTraded: toFiniteNumber(dto.mt),
  };
}

export async function searchProfilesProxy(
  params: SearchProfilesProxyParams,
): Promise<SearchProfilesProxyResult> {
  const dto = await http.get<PageResponseDto<ProfileSearchItemDto>>('/api/profiles', {
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.page !== undefined ? { page: params.page } : {}),
      ...(params.size !== undefined ? { size: params.size } : {}),
    },
  });

  return {
    profiles: (dto.content ?? []).map((item) => ({
      id: item.i,
      nickname: item.n,
      stats: mapStats(item.s),
      look: mapPlayerLook(item.lk),
    })),
    total: dto.totalElements ?? 0,
    page: (dto.number ?? 0) + 1,
    size: dto.size ?? 0,
    totalPages: dto.totalPages ?? 0,
  };
}
